import React, { useEffect, useRef, useState } from 'react';
import { Asset as ContentfulAsset } from 'contentful';
import Image from 'next/image';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import axios from 'axios';
import logger from '@greenlightme/logger';
import { captureError, trackRumEvent } from '../../utils/datadogRum';
import { useInView } from 'react-intersection-observer';
import { Asset as MediaAsset } from '../AtomicComponents/Asset';
import { useImageFormatSupport } from '../../hooks/useImageFormatSupport';

interface AppFeatureAnimationItemProps {
  media: ContentfulAsset;
  play?: boolean;
  onEnded?: () => void;
}

export function AppFeatureAnimationItem({
  media,
  play,
  onEnded
}: AppFeatureAnimationItemProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [lottieData, setLottieData] = useState(null);
  const [played, setPlayed] = useState(false);
  const contentType = media.fields.file.contentType;
  const [containerRef] = useInView({
    triggerOnce: true,
    threshold: 0.25
  });

  const assetVideoRef = useRef<HTMLVideoElement | null>(null);
  const prevPlayRef = useRef<boolean | undefined>(play);
  const hasAttemptedPlayRef = useRef(false);
  const [videoReady, setVideoReady] = useState(false);
  const [lastFramePoster, setLastFramePoster] = useState<string | null>(null);
  const [posterRequested, setPosterRequested] = useState(false);
  const preferredFormat = useImageFormatSupport();

  const handleEnded = () => {
    onEnded?.();
    setPlayed(true);
  };

  useEffect(() => {
    if (contentType.includes('json') && play && !lottieData) {
      axios
        .get(media.fields.file.url)
        .then(({ data }) => setLottieData(data))
        .catch((err) => {
          logger.error('Error fetching lottie data', err);
          console.error('Error fetching lottie data', err);
        });
    }
  }, [contentType, play, lottieData, media.fields.file.url]);

  // Generate poster from last frame when video metadata loads (on initial load or when navigating to slide)
  useEffect(() => {
    if (!contentType.includes('video')) return;
    const videoEl = assetVideoRef.current;
    if (!videoEl || posterRequested || lastFramePoster) return;

    const generatePoster = () => {
      if (!videoEl || videoEl.readyState < 1) return;

      const vw = videoEl.videoWidth || 0;
      const vh = videoEl.videoHeight || 0;
      if (!vw || !vh) return;

      setPosterRequested(true);
      const targetW = Math.min(800, vw);
      const targetH = Math.round((targetW / vw) * vh);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setPosterRequested(false);
        return;
      }

      const mime = preferredFormat === 'webp' ? 'image/webp' : 'image/jpeg';
      const onSeeked = () => {
        try {
          ctx.drawImage(videoEl, 0, 0, targetW, targetH);
          const dataUrl = canvas.toDataURL(mime, 0.8);
          if (dataUrl && typeof dataUrl === 'string') {
            setLastFramePoster(dataUrl);
          }
        } catch (err) {
          // Ignore canvas taint or unsupported toDataURL
          logger.error('Error generating poster from last frame', err);
        } finally {
          videoEl.removeEventListener('seeked', onSeeked);
          // Reset video to beginning after capturing poster
          const onResetSeeked = () => {
            videoEl.removeEventListener('seeked', onResetSeeked);
            videoEl.currentTime = 0;
          };
          videoEl.addEventListener('seeked', onResetSeeked, { once: true });
          videoEl.currentTime = 0;
        }
      };

      videoEl.addEventListener('seeked', onSeeked, { once: true });
      const endTime = Math.max(0, (videoEl.duration || 0) - 0.1);
      videoEl.currentTime = endTime;
    };

    // Generate poster when video metadata is loaded (works for initial load and when navigating to slide)
    if (videoEl.readyState >= 1) {
      generatePoster();
    } else {
      videoEl.addEventListener('loadedmetadata', generatePoster, {
        once: true
      });
    }
  }, [
    contentType,
    posterRequested,
    lastFramePoster,
    preferredFormat,
    videoReady
  ]);

  useEffect(() => {
    const videoEl = assetVideoRef.current;
    const lottie = lottieRef.current;
    const playChanged = prevPlayRef.current !== play;
    prevPlayRef.current = play;

    if (videoEl && videoReady) {
      if (play) {
        if (played) {
          videoEl.currentTime = 0;
          setPlayed(false);
          hasAttemptedPlayRef.current = false;
        }
        // Play if: play just changed to true, or we need to restart after playing, or video just became ready and we haven't attempted play yet
        if (
          playChanged ||
          played ||
          (!hasAttemptedPlayRef.current && !played)
        ) {
          hasAttemptedPlayRef.current = true;
          const playPromise = videoEl.play();
          if (playPromise !== undefined) {
            playPromise.catch((err: DOMException) => {
              if (err.name === 'NotAllowedError') {
                trackRumEvent('autoplay_blocked', {
                  page_url: window.location.href,
                  component: 'AppFeatureAnimationItem'
                });
              }
              logger.error('Error playing video', err);
              console.error('Error playing video', err);
              captureError(err);
            });
          }
        }
      } else {
        videoEl.pause();
        setPlayed(false);
        hasAttemptedPlayRef.current = false;
      }
    }

    if (lottie) {
      if (play) {
        if (played) {
          lottie.goToAndStop(0);
        }
        if (playChanged || played) {
          lottie.play();
        }
      } else {
        lottie.stop();
      }
    }
  }, [play, played, videoReady]);

  if (contentType.includes('image')) {
    const imgWidth = media.fields.file.details.image?.width;
    const imgHeight = media.fields.file.details.image?.height;
    const aspectRatio =
      imgWidth && imgHeight ? `${imgWidth}/${imgHeight}` : undefined;
    const rawUrl = media.fields.file.url;
    const httpsUrl = rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`;
    const lqipUrl = `${httpsUrl}?w=24&q=20&fm=jpg`;
    return (
      <div
        ref={containerRef}
        className="relative flex w-full items-center justify-center tablet:h-full desktop:h-full"
        style={
          aspectRatio
            ? {
                aspectRatio: aspectRatio
              }
            : {}
        }
      >
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <Image
            src={lqipUrl}
            alt=""
            width={imgWidth}
            height={imgHeight}
            className="absolute inset-0 h-full w-full object-contain"
            style={{ filter: 'blur(12px)' }}
            loader={({ src }) => src}
            unoptimized={true}
            data-testid="app-feature-animation-image-placeholder"
          />
        </div>
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <Image
            src={media.fields.file.url}
            alt={media.fields.title}
            width={imgWidth}
            height={imgHeight}
            className={`h-full w-full object-contain opacity-0 transition-opacity duration-500`}
            loader={({ src }) => src}
            unoptimized={true}
            loading="lazy"
            onLoadingComplete={(img: HTMLImageElement) => {
              img.classList.remove('opacity-0');
              img.classList.add('opacity-100');
            }}
            data-testid="app-feature-animation-image"
          />
        </div>
      </div>
    );
  }

  if (contentType.includes('video')) {
    // Show poster overlay when video is paused (not playing) or has ended
    const showPosterOverlay = lastFramePoster && (!play || played);

    return (
      <div
        className="relative flex h-full w-full items-center justify-center [&>div>video]:object-contain"
        data-testid="app-feature-animation-video"
      >
        {/* Poster overlay from last frame - shows when paused or on initial load */}
        {showPosterOverlay && (
          <div
            className="pointer-events-none absolute inset-0 z-[1] transition-opacity duration-300"
            style={{
              backgroundImage: `url(${lastFramePoster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: play && !played ? 0 : 1
            }}
            aria-hidden="true"
          />
        )}
        <MediaAsset
          asset={media}
          className="h-full w-full"
          muted
          controls={false}
          onVideoRef={(el) => {
            // Wire up ended handler once
            if (assetVideoRef.current !== el) {
              if (assetVideoRef.current) {
                assetVideoRef.current.removeEventListener('ended', handleEnded);
              }
              assetVideoRef.current = el;
              if (el) {
                el.addEventListener('ended', handleEnded);
                setVideoReady(true);
              } else {
                setVideoReady(false);
                hasAttemptedPlayRef.current = false;
              }
            }
          }}
        />
      </div>
    );
  }

  if (contentType.includes('json')) {
    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={lottieData}
        autoPlay={false}
        loop={false}
        onComplete={handleEnded}
        className="h-full w-full object-fill"
        data-testid="app-feature-animation-lottie"
      />
    );
  }

  return null;
}
